import styled from '@emotion/styled'
import { ApiMutualFundAssetRead, ApiSegregatedPortfolioAssetRead } from 'api/rtk-api'
import { useState } from 'react'

import {
  AssetInformationModal,
  Attribute,
  DateDisplay,
  LinkButton,
  Numeric,
  Percent,
  Space,
  WeightsColumns
} from '@/components/core-v2'
import { PORTFOLIO_TYPE } from '@/constants'

import { unpascal } from '../../../../util'
import { PortfolioStepState } from '../../../03-portfolio/state/portfolio-slice'
import { ApprovalBox } from '../../molecules/approval-box'
import { ConfirmationBox } from '../../molecules/confirmation-box'
import { ApprovalProps } from './types'

type Props = ApprovalProps & {
  portfolio: PortfolioStepState
}

const Hr = styled.div`
  border-top: 1px solid var(--White90);
`

export const ApprovePortfolio = ({
  isApproved,
  onChangeIsApproved,
  onClickEdit,
  portfolio
}: Props) => {
  const [inspectingAsset, setInspectingAsset] = useState<
    ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  >()

  const isFundOfFunds = portfolio?.portfolioType === PORTFOLIO_TYPE.FundOfFunds.key
  const isModelOrFundOfFunds =
    portfolio?.portfolioType === PORTFOLIO_TYPE.ModelPortfolio.key || isFundOfFunds
  const isSelfSelect = portfolio?.portfolioType === PORTFOLIO_TYPE.SelfSelect.key

  return (
    <ApprovalBox isApproved={isApproved} title="Portfolio">
      {/* These divs are needed so that the ":first-of-type" works within the Attribute components works */}
      <div>
        <Attribute name="Type">
          {`${PORTFOLIO_TYPE[portfolio?.portfolioType].key} portfolio`}
        </Attribute>

        {isModelOrFundOfFunds && (
          <>
            <Attribute name="Name">{portfolio?.model?.portfolio?.portfolioName}</Attribute>
            <Attribute name="ID">{portfolio?.model?.portfolio?.portfolioId}</Attribute>
            <Attribute name="Tax classification">
              {portfolio?.model?.portfolio?.taxClassification}
            </Attribute>
          </>
        )}

        {isSelfSelect && (
          <Attribute name="Tax classification">{portfolio?.selfSelect.taxClassification}</Attribute>
        )}

        {isModelOrFundOfFunds && portfolio?.model?.portfolio?.multiplier && (
          <Attribute name="Portfolio multiplier">
            <Numeric value={portfolio?.model?.portfolio?.multiplier} />
          </Attribute>
        )}

        {(isSelfSelect || portfolio?.model?.portfolio?.multiplier === undefined) && (
          <>
            <Attribute name="Continuous protection multiplier">
              <Numeric
                value={
                  portfolio?.selfSelect?.calculatedMultiplier ||
                  portfolio?.model?.calculatedMultiplier
                }
              />
            </Attribute>
            <Attribute name="Term protection multiplier">
              <Numeric
                value={
                  portfolio?.selfSelect?.calculatedMultiplier ||
                  portfolio?.model?.calculatedMultiplier
                }
              />
            </Attribute>
          </>
        )}

        {isModelOrFundOfFunds && (
          <Attribute name="Rebalancing type">
            {portfolio?.model?.portfolio?.rebalancingDefinition?.type}
          </Attribute>
        )}

        {isSelfSelect && (
          <Attribute name="Rebalancing type">
            {portfolio?.selfSelect?.portfolio?.rebalancingDefinition?.type}
          </Attribute>
        )}

        {portfolio?.model?.portfolio?.rebalancingDefinition?.type === 'Frequency' &&
          isModelOrFundOfFunds && (
            <Attribute name="Rebalancing frequency">
              {unpascal(portfolio?.model?.portfolio?.rebalancingDefinition?.frequency)}
            </Attribute>
          )}

        {portfolio?.selfSelect?.portfolio?.rebalancingDefinition?.type === 'Frequency' &&
          isSelfSelect && (
            <Attribute name="Rebalancing frequency">
              {unpascal(portfolio?.selfSelect?.portfolio?.rebalancingDefinition?.frequency)}
            </Attribute>
          )}

        {portfolio?.model?.portfolio?.rebalancingDefinition?.type === 'Weight' &&
          isModelOrFundOfFunds && (
            <Attribute name="Rebalancing tolerance">
              <Percent
                fixed
                numDecimalPlaces={2}
                value={
                  (portfolio?.model?.portfolio?.rebalancingDefinition?.weightsTolerance || 0) * 100
                }
              />
            </Attribute>
          )}

        {portfolio?.selfSelect?.portfolio?.rebalancingDefinition?.type === 'Weight' &&
          isSelfSelect && (
            <Attribute name="Rebalancing tolerance">
              <Percent
                fixed
                numDecimalPlaces={2}
                value={
                  (portfolio?.selfSelect?.portfolio?.rebalancingDefinition?.weightsTolerance || 0) *
                  100
                }
              />
            </Attribute>
          )}

        {isFundOfFunds && (
          <Attribute name="Start date">
            {'startDate' in (portfolio?.model.portfolio || {}) && (
              <DateDisplay value={portfolio?.model.portfolio?.startDate as string} />
            )}
          </Attribute>
        )}
      </div>
      <Space py={3}>
        <Hr />
      </Space>
      {inspectingAsset ? (
        <AssetInformationModal
          assetId={inspectingAsset.assetId}
          isOpen={Boolean(inspectingAsset)}
          onClose={() => setInspectingAsset(undefined)}
        />
      ) : null}
      <div>
        {isModelOrFundOfFunds && (
          <WeightsColumns
            isFundOfFunds={isFundOfFunds}
            portfolio={portfolio?.model.portfolio}
            targetWeights={portfolio?.model.portfolio.targetWeights}
            onClickAsset={(asset: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) =>
              setInspectingAsset(asset)
            }
          />
        )}
        {isSelfSelect &&
          portfolio?.selfSelect.portfolio.targetWeights?.map(x => (
            <Attribute
              key={x.asset.assetId}
              nameComponent={
                <LinkButton underline onClick={() => setInspectingAsset(x.asset as any)}>
                  {`${x.asset?.name} (${x.asset.assetId})`}
                </LinkButton>
              }
            >
              <Percent fixed numDecimalPlaces={2} value={x.weight * 100} />
            </Attribute>
          ))}
      </div>
      <ConfirmationBox
        elementTitle="Portfolio"
        isApproved={isApproved}
        isAre="is"
        onChangeIsApproved={onChangeIsApproved}
        onClickEdit={onClickEdit}
      />
    </ApprovalBox>
  )
}
